import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import FileUpload from "@/components/FileUpload";
import { Save, Wand2, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import { SosLogo } from "@/components/SosLogo";

const SECTIONS = [
    { key: "hero", label: "Hero (Página inicial)" },
    { key: "contact", label: "Contactos" },
    { key: "legal", label: "Textos legais" },
    { key: "logo", label: "Logo & Marca" },
    { key: "social", label: "Redes sociais" },
    { key: "analytics", label: "Analytics" },
];

export default function AdminContent() {
    const [tab, setTab] = useState("hero");
    const [s, setS] = useState(null);
    const [busy, setBusy] = useState(false);
    const [busyLogo, setBusyLogo] = useState(false);

    useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
    if (!s) return <div className="font-mono">A carregar…</div>;

    const upd = (k, v) => setS((p) => ({ ...p, [k]: v }));

    const save = async () => {
        setBusy(true);
        try {
            const { data } = await api.put("/admin/settings", s);
            setS(data);
            toast.success("Conteúdos guardados");
        } catch {
            toast.error("Erro a guardar");
        } finally {
            setBusy(false);
        }
    };

    const generateLogo = async () => {
        if (!window.confirm("Gerar novo logótipo via IA (Gemini Nano Banana)? Pode demorar 10-30 segundos.")) return;
        setBusyLogo(true);
        try {
            const { data } = await api.post("/admin/generate-logo");
            const fullUrl = `${API.replace(/\/api$/, "")}${data.url}`;
            upd("logo_url", fullUrl);
            toast.success("Logótipo gerado e guardado");
            // auto-save
            const updated = { ...s, logo_url: fullUrl };
            await api.put("/admin/settings", updated);
            setS(updated);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro ao gerar logótipo");
        } finally {
            setBusyLogo(false);
        }
    };

    const downloadSvg = () => {
        const svg = document.getElementById("sos-logo-export");
        if (!svg) return;
        const xml = new XMLSerializer().serializeToString(svg);
        const blob = new Blob([xml], { type: "image/svg+xml" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "sostoners-logo.svg";
        a.click();
    };

    return (
        <div data-testid="admin-content-page">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                <h1 className="font-display font-black text-4xl tracking-tighter">Conteúdos</h1>
                <button onClick={save} disabled={busy} className="btn-primary disabled:opacity-50" data-testid="save-content-btn">
                    {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                    {busy ? "A guardar…" : "Guardar"}
                </button>
            </div>

            <div className="flex gap-1 mb-6 border-b-2 border-ink overflow-x-auto">
                {SECTIONS.map((sec) => (
                    <button
                        key={sec.key}
                        onClick={() => setTab(sec.key)}
                        className={`px-5 py-3 text-sm font-bold uppercase tracking-wider whitespace-nowrap border-2 border-b-0 -mb-[2px] ${
                            tab === sec.key
                                ? "border-ink bg-white text-ink"
                                : "border-transparent text-ink/50 hover:text-ink"
                        }`}
                        data-testid={`tab-${sec.key}`}
                    >
                        {sec.label}
                    </button>
                ))}
            </div>

            <div className="border-2 border-ink bg-white p-6">
                {tab === "hero" && (
                    <div className="space-y-5">
                        <Field label="Título principal" v={s.hero_title} on={(v) => upd("hero_title", v)} />
                        <Field label="Título destaque (magenta)" v={s.hero_title_accent} on={(v) => upd("hero_title_accent", v)} />
                        <TextArea label="Subtítulo" v={s.hero_subtitle} on={(v) => upd("hero_subtitle", v)} />
                    </div>
                )}
                {tab === "contact" && (
                    <div className="space-y-5">
                        <Field label="Telefone" v={s.contact_phone} on={(v) => upd("contact_phone", v)} />
                        <Field label="Email" v={s.contact_email} on={(v) => upd("contact_email", v)} />
                        <Field label="Morada" v={s.contact_address} on={(v) => upd("contact_address", v)} />
                        <Field label="Nome empresa" v={s.company_name} on={(v) => upd("company_name", v)} />
                        <Field label="NIF" v={s.company_nif} on={(v) => upd("company_nif", v)} />
                        <div className="border-t-2 border-ink pt-4">
                            <Field
                                label="Taxa de IVA em vigor (%)"
                                type="number"
                                step="0.01"
                                v={s.vat_rate ?? 23}
                                on={(v) => upd("vat_rate", Number(v))}
                                data-testid="vat-rate-input"
                            />
                            <p className="text-xs font-mono text-ink/60 mt-2">
                                Acresce a todos os preços apresentados na loja. Default Portugal continental: 23%.
                            </p>
                        </div>
                    </div>
                )}
                {tab === "legal" && (
                    <div className="space-y-5">
                        <TextArea label="Termos & Condições" v={s.legal_terms} on={(v) => upd("legal_terms", v)} rows={6} />
                        <TextArea label="Política de Privacidade" v={s.legal_privacy} on={(v) => upd("legal_privacy", v)} rows={6} />
                        <TextArea label="Política de Devoluções" v={s.legal_returns} on={(v) => upd("legal_returns", v)} rows={6} />
                    </div>
                )}
                {tab === "logo" && (
                    <div className="space-y-6">
                        <div>
                            <div className="label-mono mb-3">Logótipo SVG (sempre disponível)</div>
                            <div className="border-2 border-ink p-6 bg-paper inline-block">
                                <svg id="sos-logo-export" viewBox="0 0 320 120" width="240" height="90" xmlns="http://www.w3.org/2000/svg">
                                    <g transform="translate(0 8)">
                                        <path d="M30 8c5-6 18-10 30-4 8 4 8 12 4 18 6 4 8 14 0 22-10 10-30 8-40-2-8-8-6-26 6-34z" fill="#E6007E" />
                                        <circle cx="56" cy="6" r="4" fill="#E6007E" />
                                        <circle cx="62" cy="42" r="3" fill="#E6007E" />
                                    </g>
                                    <text x="14" y="40" fontFamily="Cabinet Grotesk, sans-serif" fontWeight="900" fontSize="22" fill="#FFFFFF" letterSpacing="-1">SOS</text>
                                    <text x="86" y="56" fontFamily="Cabinet Grotesk, sans-serif" fontWeight="900" fontSize="48" fill="#0A0A0A" letterSpacing="-2">TONERS</text>
                                    <text x="86" y="84" fontFamily="JetBrains Mono, monospace" fontWeight="600" fontSize="11" letterSpacing="3" fill="rgba(10,10,10,0.65)">TONERS · INFORMÁTICA · 2010</text>
                                </svg>
                            </div>
                            <div className="mt-3">
                                <button onClick={downloadSvg} className="btn-secondary" data-testid="download-svg-btn">
                                    <Download size={14} /> Descarregar SVG
                                </button>
                            </div>
                        </div>

                        <div className="border-t-2 border-ink pt-6">
                            <div className="label-mono mb-3">Logótipo personalizado (substitui o SVG no website)</div>
                            <FileUpload
                                value={s.logo_url}
                                onChange={(v) => upd("logo_url", v)}
                                label=""
                                testid="logo-upload"
                            />
                            <button
                                onClick={generateLogo}
                                disabled={busyLogo}
                                className="btn-primary mt-3 disabled:opacity-50"
                                data-testid="generate-logo-btn"
                            >
                                {busyLogo ? <Loader2 className="animate-spin" size={14} /> : <Wand2 size={14} />}
                                {busyLogo ? "A gerar via IA…" : "Gerar novo logo via IA (Gemini)"}
                            </button>
                            <p className="mt-3 text-xs font-mono text-ink/60">
                                A IA cria uma versão PNG moderna do logótipo SOSTONERS. O logo carregado/gerado substitui automaticamente o SVG no website.
                            </p>
                        </div>
                    </div>
                )}
                {tab === "social" && (
                    <div className="space-y-5">
                        <Field label="Facebook URL" v={s.facebook} on={(v) => upd("facebook", v)} />
                        <Field label="Instagram URL" v={s.instagram} on={(v) => upd("instagram", v)} />
                        <Field label="LinkedIn URL" v={s.linkedin} on={(v) => upd("linkedin", v)} />
                    </div>
                )}
                {tab === "analytics" && (
                    <div className="space-y-6" data-testid="analytics-section">
                        <div>
                            <Field
                                label="Google Analytics ID (GA4)"
                                v={s.ga_tracking_id}
                                on={(v) => upd("ga_tracking_id", v)}
                                data-testid="ga-tracking-input"
                            />
                            <p className="text-xs font-mono text-ink/60 mt-2">
                                Formato: <code>G-XXXXXXXXXX</code>. Obtenha em{" "}
                                <a
                                    className="underline"
                                    href="https://analytics.google.com"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    analytics.google.com
                                </a>
                                {" "}→ Admin → Streams de dados → ID de medição.
                            </p>
                        </div>
                        <div className="border-t-2 border-ink pt-5">
                            <Field
                                label="Google Tag Manager ID (opcional)"
                                v={s.gtm_id}
                                on={(v) => upd("gtm_id", v)}
                                data-testid="gtm-input"
                            />
                            <p className="text-xs font-mono text-ink/60 mt-2">
                                Formato: <code>GTM-XXXXXXX</code>. Use GTM se quiser gerir múltiplas tags via interface visual.
                            </p>
                        </div>
                        <div className="border-t-2 border-ink pt-5">
                            <Field
                                label="Facebook Pixel ID (opcional)"
                                v={s.fb_pixel_id}
                                on={(v) => upd("fb_pixel_id", v)}
                                data-testid="fb-pixel-input"
                            />
                            <p className="text-xs font-mono text-ink/60 mt-2">
                                Para tracking de conversões em campanhas Meta/Facebook Ads.
                            </p>
                        </div>
                        <div className="border-t-2 border-ink pt-5 bg-paper p-4 -mx-2">
                            <p className="text-xs font-mono text-ink/70 leading-relaxed">
                                <strong>Nota:</strong> Os scripts são carregados dinamicamente apenas no front-office público. As páginas <code>/admin/*</code> são automaticamente excluídas do tracking. Após guardar, recarregue a página da loja para activar.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function Field({ label, v, on, type = "text", step }) {
    return (
        <label className="block">
            <span className="label-mono mb-2 block">{label}</span>
            <input type={type} step={step} value={v ?? ""} onChange={(e) => on(e.target.value)} className="input-edge" />
        </label>
    );
}
function TextArea({ label, v, on, rows = 3 }) {
    return (
        <label className="block">
            <span className="label-mono mb-2 block">{label}</span>
            <textarea value={v || ""} onChange={(e) => on(e.target.value)} rows={rows} className="input-edge" />
        </label>
    );
}
